import { Event, LocalAdapter, Storage, useStore } from '@mirai/data-sources';
import { parseDate, useLocale } from '@mirai/locale';
import { ServiceCountry } from '@mirai/services';
import { InputText, Form as FormBase, Modal, ScrollView, styles, Text, useDevice } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getSafeTrust } from '../../../trust';
import { BookingTerms, Payment } from '../../__shared__';
import { EVENT, generateId, getCountryCode, METRICS_EVENTS } from '../../helpers';
import { DISCOUNT_TYPE } from '../../Rates/Rates.constants';
import { FIELD, FORM_STORAGE_CACHE, FORM_STORAGE_KEY, INPUT } from '../Checkout.constants';
import { L10N } from '../Checkout.l10n';
import * as style from '../Checkout.module.css';
import { getFieldProps, getSchemaProps } from '../helpers';

const storage = new Storage({ adapter: LocalAdapter, cache: FORM_STORAGE_CACHE });

const key = (dataSource, schema) =>
  `${FORM_STORAGE_KEY}:${generateId(
    JSON.stringify({
      ...dataSource,
      schema: Object.entries(schema)
        .filter(([, value]) => value.input !== INPUT.HTML)
        .map(([key]) => key),
    }),
  )}`;

const Form = ({
  children,
  dataSource: { currency, repositories: { countries } = {}, form: schema = {}, tracking = {}, ...dataSource } = {},
  showErrors = false,
  onError = () => {},
  ...others
}) => {
  const { isDesktop } = useDevice();
  const { currencyFormat, translate } = useLocale();
  const { set, value: store = {} } = useStore();
  const { club, language, payment: storePayment, session } = store;
  const trust = getSafeTrust(store);

  const clubDiscount =
    dataSource?.price?.discounts?.find?.((discount) => discount?.type === DISCOUNT_TYPE.CLUB)?.amount || 0;
  const fieldContext = { clubDiscount, currency, currencyFormat };

  const formReady = useRef(false);
  const restoredForm = useRef(storage.get(key(dataSource, schema)));

  const [errorForm, setErrorForm] = useState({});
  const [errorPayment, setErrorPayment] = useState({});
  const [form, setForm] = useState(
    restoredForm.current || Object.fromEntries(Object.entries(schema).map(([key, { value }]) => [key, value])),
  );
  const [formTracked, setFormTracked] = useState();
  const [modal, setModal] = useState(false);

  useEffect(() => {
    trust.record('formInitialized', {
      source: restoredForm.current ? 'cache' : 'schema',
      count: Object.entries(schema).filter(([name, field]) => {
        const value = restoredForm.current ? restoredForm.current[name] : field?.value;
        return value !== undefined && value !== null && value !== '' && value !== false;
      }).length,
    });
  }, [schema, trust]);

  useEffect(() => {
    trust.prepareContact(form);
  }, [form, trust]);

  useEffect(() => {
    trust.record('formInitialized', {
      source: 'session',
      count: Object.values(getSchemaProps({ schema, value: session })).filter(
        (value) => value !== undefined && value !== null && value !== '' && value !== false,
      ).length,
    });
  }, [schema, session, trust]);

  useEffect(() => {
    setForm({ ...form, ...getSchemaProps({ schema, value: session }) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useLayoutEffect(() => {
    if (Object.keys(form).length) storage.set(key(dataSource, schema), form);
    set({ checkout: form });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  useEffect(() => {
    onError({ ...errorForm, ...errorPayment });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorForm, errorPayment]);

  useEffect(() => {
    if (formReady.current) return;
    const hasRequiredValues = getFields(form)
      .filter(([, field]) => field.required)
      .map(([key]) => key)
      .every((key) => form[key] !== undefined && form[key] !== '');
    const hasErrors = Object.keys(errorForm).length > 0 || Object.keys(errorPayment).length > 0;

    if (!hasErrors && hasRequiredValues && Object.keys(storePayment || {}).length > 0) {
      Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.CHECKOUT_INTERACTION, action: 'form_ready' });
      formReady.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorForm, errorPayment, storePayment]);

  const handleChange = async (value) => {
    const next = { ...form, ...value };

    if (!!schema.country && next.country && !next.phone) {
      const { code } = await ServiceCountry.info(getCountryCode(next.country, countries));
      if (code) next.phone = code;
    }

    const nextFields = getFields(next);
    const nextFiltered =
      nextFields.length < getFields(form).length
        ? Object.fromEntries(Object.entries(next).filter(([key]) => Object.fromEntries(nextFields)[key] !== undefined))
        : undefined;

    setForm(nextFiltered || next);
  };

  const handleLeave = () => {
    const { email, firstName, lastName, phone } = errorForm;

    if (!formTracked && !email && !firstName && !lastName && !phone) {
      Event.publish(EVENT.CHECKOUT_FORM, { event: EVENT.CHECKOUT_FORM, response: { tracking } });
      setFormTracked(true);
    }
  };

  const handleFormError = (errors) => {
    const previous = Object.keys(errorForm).filter((key) => errorForm[key]);
    const current = Object.keys(errors || {}).filter((key) => errors[key]);
    trust.record('validation', {
      count: current.length,
      resolved: previous.filter((key) => !current.includes(key)).length,
    });
    setErrorForm(errors);
  };

  const getFields = (value) =>
    Object.entries(schema)
      .filter(([key]) => !!schema[key])
      .filter(([, { visible }]) => !visible || visible(value));

  const { payment } = dataSource || {};
  const { testId } = others;
  const formProps = {
    ...others,
    autoComplete: 'off',
    showErrors,
    validateOnMount: true,
    onChange: handleChange,
    onError: handleFormError,
    onLeave: handleLeave,
    className: styles(style.form, style.box),
    'data-trust-fields': 'true',
    'data-trust-scope': trust.scope,
  };

  return (
    <>
      <FormBase {...formProps} testId={`${testId}-required`}>
        <Text bold headline={isDesktop} wide>
          {translate(L10N.LABEL_PERSONAL_DETAILS)}
        </Text>

        {getFields(form)
          .filter(([, field]) => field.required)
          .map(([key, field = {}]) =>
            React.createElement(
              FIELD[field.input] || InputText,
              getFieldProps(
                key,
                field,
                {
                  value: form[key],
                  error: errorForm[key],
                  language,
                  session,
                  translate,
                  ...dataSource,
                  ...fieldContext,
                },
                club,
                () => setModal(true),
              ),
            ),
          )}
      </FormBase>

      {children}

      {payment && (
        <Payment
          {...{
            ...payment,
            info: { ...payment.info, date: payment.info?.date ? parseDate(payment.info?.date) : undefined },
            showErrors,
          }}
          currency={currency}
          requiredText={translate(L10N.LABEL_REQUIRED)}
          onError={setErrorPayment}
        />
      )}

      <FormBase {...formProps} testId={`${testId}-adittional`}>
        <Text bold headline={isDesktop} wide>
          {translate(L10N.LABEL_ADDITIONAL_INFO)}
        </Text>
        {getFields(form)
          .filter(([, field]) => !field.required)
          .map(([key, field = {}]) =>
            React.createElement(
              FIELD[field.input] || InputText,
              getFieldProps(
                key,
                field,
                {
                  value: form[key],
                  error: errorForm[key],
                  language,
                  session,
                  translate,
                  ...dataSource,
                  ...fieldContext,
                },
                club,
                () => setModal(true),
              ),
            ),
          )}
      </FormBase>

      <Modal
        fit
        title={translate(L10N.LABEL_CONDITIONS)}
        visible={modal}
        onClose={() => setModal(false)}
        className={style.modal}
      >
        <ScrollView snap={false} vertical className={style.scrollview}>
          <BookingTerms dataSource={dataSource} small className={style.others} />
        </ScrollView>
      </Modal>
    </>
  );
};

Form.displayName = 'Mirai:Core:Checkout:Form';

Form.propTypes = {
  children: PropTypes.node,
  dataSource: PropTypes.shape({
    form: PropTypes.shape({}),
  }),
  showErrors: PropTypes.bool,
  onError: PropTypes.func,
};

export { Form };
